import * as React from 'react';
import { useMsal } from '@azure/msal-react';
import { getClaim, getFullName } from 'auth/account';
import { styled } from '@mui/material/styles';
import { MenuAction } from 'models/types';
import Link from '@mui/material/Link';
import IconButton from '@mui/material/IconButton';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import Paper from '@mui/material/Paper';
import { signInAuthProvider, getEditProfileRequest, getPasswordChangeRequest } from 'auth';
import LocaleButton from 'components/LocaleButton';
import WsNotificationButton from 'components/WsNotificationButton';
import { useTranslation } from 'react-i18next';
import { Logout } from 'services/user';

const PREFIX = 'ProfileNav';

const classes = {
  root: `${PREFIX}-root`,
  locale: `${PREFIX}-locale`,
  notification: `${PREFIX}-notification`,
  logout: `${PREFIX}-logout`,
  flex1: `${PREFIX}-flex1`,
  profile: `${PREFIX}-profile`,
  leftMenu: `${PREFIX}-leftMenu`,
};

const Root = styled('div')(({ theme }) => ({
  [`&.${classes.root}`]: {
    display: 'flex',
    alignItems: 'center',
  },
  [`& .${classes.locale}`]: {
    flex: 1,
    paddingRight: 20,
  },
  [`& .${classes.notification}`]: {
    flex: 1,
    paddingRight: 20,
  },

  [`& .${classes.logout}`]: {
    flex: 1,
    fontSize: 16,
    color: '#4050b5',
    paddingRight: 20,
  },

  [`& .${classes.flex1}`]: {
    flex: 1,
  },

  [`& .${classes.profile}`]: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1),
  },

  [`& .${classes.leftMenu}`]: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    whiteSpace: 'nowrap',
  },
}));

interface Props {
  actions?: MenuAction[];
  hideNotification?: boolean;
}

export default function ProfileNav(props: Props): JSX.Element {
  const { accounts, instance } = useMsal();
  const { actions, hideNotification } = props;
  const jobTitle = getClaim(accounts[0], 'jobTitle');
  const fullName = getFullName(accounts[0]);
  const [open, setOpen] = React.useState(false);
  const { t } = useTranslation('default');
  const anchorRef = React.useRef<HTMLButtonElement>(null);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }

    setOpen(false);
  };

  const handleEditProfile = () => {
    signInAuthProvider.loginRedirect(getEditProfileRequest());
  };

  const handleChangePassword = () => {
    signInAuthProvider.loginRedirect(getPasswordChangeRequest());
  };

  const handleLogout = () => {
    Logout(instance);
  };

  const generateMenuItems = (): MenuAction[] => {
    const defaultMenuItems = [
      {
        label: t('edit-profile'),
        onClick: handleEditProfile,
      },
      {
        label: t('change-password'),
        onClick: handleChangePassword,
      },
    ];
    if (actions) {
      return defaultMenuItems.concat(actions);
    } else {
      return defaultMenuItems;
    }
  };

  return (
    <Root className={classes.root}>
      <div className={classes.locale}>
        <LocaleButton />
      </div>
      <div className={classes.logout}>
        <Link color="inherit" href="#" onClick={handleLogout} underline="hover">
          {t('logout')}
        </Link>
      </div>
      {!hideNotification && (
        <div className={classes.notification}>
          <WsNotificationButton />
        </div>
      )}
      <div className={classes.profile}>
        <div className={classes.leftMenu}>
          {fullName && <div className={classes.flex1}>{fullName || t('unknown')}</div>}
          {jobTitle && <div className={classes.flex1}>{jobTitle}</div>}
        </div>
        <IconButton ref={anchorRef} color="inherit" onClick={handleToggle} size="large">
          <AccountCircleIcon fontSize="large" />
        </IconButton>
        <ClickAwayListener onClickAway={handleClose}>
          <Paper>
            <Menu
              id="profile-nav-menu"
              elevation={0}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
              anchorEl={anchorRef.current}
              keepMounted
              open={open}
              onClose={handleClose}
            >
              {generateMenuItems().map((m: MenuAction, i: number) => (
                <MenuItem key={i} onClick={m.onClick}>
                  {m.label}
                </MenuItem>
              ))}
            </Menu>
          </Paper>
        </ClickAwayListener>
      </div>
    </Root>
  );
}
